import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Paper,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  GetSlate,
  SaveSlate,
  UpdateSlate,
} from "../../../../services/TestLibService";
import CommonLoader from "../../../common/CommonLoader";
import SlateTests from "./SlateTests";

const AddEditSlate = () => {
  const { jobId: jobIdParam } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(jobIdParam);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [jobId, setJobId] = useState("");
  const [jobCategory, setJobCategory] = useState("");
  const [jobName, setJobName] = useState("");
  const [dateCreated, setDateCreated] = useState("");
  const [dateWritten, setDateWritten] = useState("");
  const [glCode, setGlCode] = useState("");
  const [listPrice, setListPrice] = useState("");
  const [adjustedPrice, setAdjustedPrice] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [comments, setComments] = useState("");
  const [active, setActive] = useState(false);
  const [recordid, setRecordId] = useState("");
  const [laboratoryCost, setLaboratoryCost] = useState("");
  const [minimumPrice, setMinimumPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [billByTest, setBillByTest] = useState(false);
  const [validationprocessId, setValidationProcessId] = useState("");
  const [billingprocessId, setBillingProcessId] = useState("");
  const [taDays, setTaDays] = useState("");
  const [coc, setCoc] = useState("");
  const [sampleBottle, setSampleBottle] = useState("");
  const [preservation, setPreservation] = useState("");
  const [billable, setBillable] = useState(false);

  useEffect(() => {
    if (isEdit) fetchSlate();
  }, [jobIdParam]);

  const fetchSlate = async () => {
    setLoading(true);
    try {
      const response = await GetSlate(jobIdParam.trim());
      const s = response?.results ?? response;
      if (!s) return;
      populateFields(s);
    } catch {
      NotificationManager.error("Error loading slate record");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toISOString().split("T")[0];
  };

  const populateFields = (s) => {
    setJobId(s.jobId || "");
    setJobCategory(s.jobCategory || "");
    setJobName(s.jobName || "");
    setDateCreated(formatDate(s.dateCreated));
    setDateWritten(formatDate(s.dateWritten));
    setGlCode(s.glCode || "");
    setListPrice(s.listPrice ?? "");
    setAdjustedPrice(s.adjustedPrice ?? "");
    setJobDescription(s.jobDescription || "");
    setComments(s.comments || "");
    setActive(s.active || false);
    setRecordId(s.recordid ?? "");
    setLaboratoryCost(s.laboratoryCost ?? "");
    setMinimumPrice(s.minimumPrice ?? "");
    setDiscountPercent(s.discountPercent ?? "");
    setBillByTest(s.billbytest || false);
    setValidationProcessId(s.validationprocessId || "");
    setBillingProcessId(s.billingprocessId || "");
    setTaDays(s.taDays ?? "");
    setCoc(s.coc || "");
    setSampleBottle(s.sampleBottle || "");
    setPreservation(s.preservation || "");
    setBillable(s.billable || false);
  };

  const buildPayload = () => ({
    jobId,
    jobCategory,
    jobName,
    dateCreated: dateCreated ? new Date(dateCreated).toISOString() : null,
    dateWritten: dateWritten ? new Date(dateWritten).toISOString() : null,
    glCode,
    listPrice: parseFloat(listPrice) || null,
    adjustedPrice: parseFloat(adjustedPrice) || null,
    jobDescription,
    comments,
    active,
    recordid: parseInt(recordid) || 0,
    laboratoryCost: parseFloat(laboratoryCost) || null,
    minimumPrice: parseFloat(minimumPrice) || null,
    discountPercent: parseFloat(discountPercent) || null,
    billbytest: billByTest,
    validationprocessId,
    billingprocessId,
    taDays: parseInt(taDays) || null,
    coc,
    sampleBottle,
    preservation,
    billable,
  });

  const handleSubmit = async () => {
    if (!jobId) {
      NotificationManager.error("Job ID is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await UpdateSlate(buildPayload());
        NotificationManager.success("Slate updated successfully");
      } else {
        await SaveSlate(buildPayload());
        NotificationManager.success("Slate saved successfully");
      }
    } catch {
      NotificationManager.error("Error saving slate");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate("/vlp/maintenance-test-slate");
    }
  };

  const tf = (label, value, setter, extra = {}) => (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => setter(e.target.value)}
      fullWidth
      {...extra}
    />
  );

  const cb = (label, checked, setter) => (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={Boolean(checked)}
          onChange={(e) => setter(e.target.checked)}
        />
      }
      label={label}
    />
  );

  return (
    <div className="vlp-component-container">
      <span className="title">{isEdit ? "Edit Slate" : "Add Slate"}</span>
      <hr />

      <Paper elevation={2} sx={{ p: 2 }}>
        <Grid container spacing={1.5}>

          {/* Row 1 */}
          <Grid item xs={2}>
            {tf("Job ID *", jobId, setJobId, {
              disabled: isEdit,
              inputProps: { maxLength: 10 },
            })}
          </Grid>
          <Grid item xs={2}>{tf("Job Category", jobCategory, setJobCategory, { inputProps: { maxLength: 50 } })}</Grid>
          <Grid item xs={2}>{tf("Job Name", jobName, setJobName, { inputProps: { maxLength: 100 } })}</Grid>
          <Grid item xs={2}>
            <TextField size="small" label="Date Created" type="date" fullWidth
              value={dateCreated}
              onChange={(e) => setDateCreated(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={2}>
            <TextField size="small" label="Date Written" type="date" fullWidth
              value={dateWritten}
              onChange={(e) => setDateWritten(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={2}>{tf("GL Code", glCode, setGlCode, { inputProps: { maxLength: 20 } })}</Grid>

          {/* Row 2 */}
          <Grid item xs={2}>{tf("List Price", listPrice, setListPrice, { type: "number" })}</Grid>
          <Grid item xs={2}>{tf("Adjusted Price", adjustedPrice, setAdjustedPrice, { type: "number" })}</Grid>
          <Grid item xs={2}>{tf("Job Description", jobDescription, setJobDescription, { inputProps: { maxLength: 500 } })}</Grid>
          <Grid item xs={2}>{tf("Comments", comments, setComments, { inputProps: { maxLength: 250 } })}</Grid>
          <Grid item xs={2}>{tf("Record ID", recordid, setRecordId, { type: "number" })}</Grid>
          <Grid item xs={2}>{tf("Laboratory Cost", laboratoryCost, setLaboratoryCost, { type: "number" })}</Grid>

          {/* Row 3 */}
          <Grid item xs={2}>{tf("Minimum Price", minimumPrice, setMinimumPrice, { type: "number" })}</Grid>
          <Grid item xs={2}>{tf("Discount Percent", discountPercent, setDiscountPercent, { type: "number" })}</Grid>
          <Grid item xs={2}>{tf("Validation Process ID", validationprocessId, setValidationProcessId, { inputProps: { maxLength: 20 } })}</Grid>
          <Grid item xs={2}>{tf("Billing Process ID", billingprocessId, setBillingProcessId, { inputProps: { maxLength: 20 } })}</Grid>
          <Grid item xs={2}>{tf("TA Days", taDays, setTaDays, { type: "number" })}</Grid>
          <Grid item xs={2}>{tf("COC", coc, setCoc, { inputProps: { maxLength: 20 } })}</Grid>

          {/* Row 4 */}
          <Grid item xs={2}>{tf("Sample Bottle", sampleBottle, setSampleBottle, { inputProps: { maxLength: 50 } })}</Grid>
          <Grid item xs={2}>{tf("Preservation", preservation, setPreservation, { inputProps: { maxLength: 50 } })}</Grid>

          {/* Checkboxes */}
          <Grid item xs={2}>{cb("Active", active, setActive)}</Grid>
          <Grid item xs={2}>{cb("Bill By Test", billByTest, setBillByTest)}</Grid>
          <Grid item xs={2}>{cb("Billable", billable, setBillable)}</Grid>

          {/* Actions */}
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
            <Button variant="outlined" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Update" : "Save"}
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* Tests assigned to the slate. Only available once the slate exists, since the
          tests are stored against its job id. */}
      {isEdit && <SlateTests jobId={jobId || jobIdParam} />}

      <CommonLoader loading={loading || saving} />
    </div>
  );
};

export default AddEditSlate;
